import { motion } from "framer-motion";
import { Moon, Twitter, Instagram, Heart } from "lucide-react";

export default function Footer() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="footer-section" className="relative py-16 border-t border-purple-500/15">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 60% 60% at 50% 100%, rgba(109,40,217,0.06) 0%, transparent 70%)"
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Moon size={14} className="text-white" />
              </div>
              <span className="font-serif font-bold text-lg gradient-text">DreamWeaver</span>
            </div>
            <p className="text-sm text-slate-500 max-w-xs leading-relaxed mb-6">
              Personalized bedtime stories and watercolor illustrations crafted just for your child — every magical night.
            </p>

            {/* Newsletter */}
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="your@email.com"
                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-amber-400/40 transition-all min-w-0"
              />
              <button className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/20 transition-all whitespace-nowrap">
                Subscribe
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-2">New story themes and updates, straight to your inbox.</p>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">Product</h4>
            <ul className="space-y-2.5">
              {[
                { label: "Story Generator", id: "generator" },
                { label: "Voice Narration", id: "voices" },
                { label: "Pricing", id: "pricing" },
                { label: "How It Works", id: "manifesto" },
              ].map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l.id)}
                    className="text-sm text-slate-500 hover:text-amber-300 transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Trust */}
          <div>
            <h4 className="text-xs font-mono tracking-widest uppercase text-slate-500 mb-4">Trust & Safety</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 text-xs">✓</span>
                </div>
                <span className="text-xs text-slate-500">Child-safe content</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 text-xs">✓</span>
                </div>
                <span className="text-xs text-slate-500">No scary imagery</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 text-xs">✓</span>
                </div>
                <span className="text-xs text-slate-500">Age-appropriate language</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-400 text-xs">⚡</span>
                </div>
                <span className="text-xs text-slate-500">Powered by Gemini AI</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-purple-500/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-600 flex items-center gap-1.5">
            Made with <Heart size={11} className="text-rose-500 fill-rose-500" /> for every child's magical bedtime
          </p>
          <p className="text-xs text-slate-600">
            © {new Date().getFullYear()} DreamWeaver. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
